<template>
  <div class="pa-10">
    <title-page icon="mdi-lightbulb-on-outline">
      {{ $t('billing-control.opportunities.title') }}
    </title-page>

    <v-breadcrumbs
        :items="[
            {text: $t('reports.reports'), to: {name: 'reports'}, exact: true},
            {text: $t('billing-control.workspace'), to: {name: 'reports.billing-control'}, exact: true},
            {text: $t('billing-control.opportunities.title')},
        ]"
        divider=">"
    ></v-breadcrumbs>

    <v-row align="center" class="mt-2">
      <v-col cols="12" md="4">
        <v-select
            :label="$t('billing-control.table.category')"
            :items="categoryItems"
            v-model="category"
            clearable
            dense
            hide-details
            outlined
            @change="reload"
        />
      </v-col>
    </v-row>

    <v-alert dense text color="primary" class="mt-2">
      <b>{{ summary.assets }}</b> {{ $t('billing-control.opportunities.assets-not-licensed') }} ·
      <b>{{ summary.organizations }}</b> {{ $t('billing-control.opportunities.organizations') }} ·
      <b>{{ numberFormat(summary.monthlyPotential) }} €</b> {{ $t('billing-control.opportunities.potential') }}
      <help-tip :text="$t('billing-control.help.opportunities.title')"/>
    </v-alert>

    <v-data-table
        :headers="headers"
        :items="items"
        :loading="loading"
        :options.sync="options"
        :server-items-length="total"
        :footer-props="{'items-per-page-options': [25, 50, 100]}"
        class="elevation-1"
        style="cursor: pointer"
        @click:row="openDetail"
    >
      <template v-for="(key, column) in explainedColumns" #[`header.${column}`]="{ header }">
        {{ header.text }} <help-tip :key="column" :text="$t('billing-control.help.opportunities.' + key)"/>
      </template>
      <template #item.organization="{item}">{{ item.organizationName || item.organizationId || '—' }}</template>
      <template #item.assetType="{item}">{{ $t('billing-control.opportunities.asset-types.' + item.assetType) }}</template>
      <template #item.category="{item}">{{ item.category ? $t('enums.subscription-plan-types.' + item.category) : '—' }}</template>
      <template #item.unitMonthlyPrice="{item}">{{ numberFormat(item.unitMonthlyPrice) }} €</template>
      <template #item.monthlyPotential="{item}"><b class="primary--text">{{ numberFormat(item.monthlyPotential) }} €</b></template>
      <template #item.reason="{item}">{{ reasonLabel(item) }}</template>
      <template #item.actions="{item}">
        <v-icon small @click.stop="openDetail(item)">mdi-eye-outline</v-icon>
      </template>
    </v-data-table>

    <!-- Le détail d'une ligne : quoi, pourquoi, chez qui, quel abonnement (actif), et d'où vient le prix. -->
    <v-navigation-drawer v-model="drawer" right temporary fixed width="420">
      <div v-if="detail" class="pa-4">
        <div class="d-flex justify-space-between align-center">
          <h3>{{ $t('billing-control.opportunities.detail.title') }}</h3>
          <v-btn icon small @click="drawer = false"><v-icon>mdi-close</v-icon></v-btn>
        </div>

        <div class="mt-4">
          <div class="caption text-uppercase grey--text">{{ $t('billing-control.table.organization') }}</div>
          <div class="font-weight-bold">{{ detail.organizationName || detail.organizationId || '—' }}</div>
        </div>
        <div class="mt-3">
          <div class="caption text-uppercase grey--text">{{ $t('billing-control.opportunities.reason') }}</div>
          <div class="error--text">{{ reasonLabel(detail) }}</div>
          <div class="text-caption mt-1">{{ $t('billing-control.opportunities.detail.explain.' + detail.reason, { licence: categoryLabel(detail.category) }) }}</div>
        </div>
        <div class="mt-3">
          <div class="caption text-uppercase grey--text">{{ $t('billing-control.opportunities.potential') }}</div>
          <div class="text-h6 primary--text">{{ numberFormat(detail.monthlyPotential) }} €</div>
          <div class="text-caption">{{ $t('billing-control.opportunities.detail.price', { count: detail.assetCount, price: numberFormat(detail.unitMonthlyPrice), licence: categoryLabel(detail.category) }) }}</div>
        </div>

        <v-divider class="my-3"/>
        <div class="caption text-uppercase grey--text">{{ $t('billing-control.opportunities.detail.assets', { count: detail.assetCount }) }}</div>
        <div v-for="(asset, i) in detail.assets" :key="asset.id || i" class="asset-row">
          <router-link v-if="asset.id" :to="assetRoute(detail, asset)" target="_blank" class="font-weight-bold">{{ asset.name || asset.id }}</router-link>
          <span v-else class="font-weight-bold">{{ asset.name || '—' }}</span>
          <div v-for="member in asset.members" :key="member.id" class="text-caption">
            {{ $t('billing-control.opportunities.detail.at-home-of') }}
            <router-link :to="{ name: 'member.edit', params: { id: detail.organizationId, memberId: member.id } }" target="_blank">{{ [member.firstname, member.lastname].filter(Boolean).join(' ') || member.id }}</router-link>
          </div>
          <div v-for="subscription in asset.expenseSubscriptions" :key="subscription.id" class="text-caption">
            {{ $t('billing-control.opportunities.detail.not-covered-by') }}
            <router-link :to="{ name: 'organizations.subscriptions.edit', params: { id: detail.organizationId, subscriptionId: subscription.id } }" target="_blank">{{ $t('billing-control.opportunities.detail.open-subscription') }}</router-link>
          </div>
        </div>
      </div>
    </v-navigation-drawer>
  </div>
</template>

<script>
import BillingControlRepository from "@repository/BillingControlRepository"
import HelpTip from "./HelpTip.vue"

export default {
  components: { HelpTip },

  data() {
    return {
      explainedColumns: {assetType: 'asset-type', reason: 'reason', category: 'missing-licence', assetCount: 'asset-count', unitMonthlyPrice: 'unit-monthly-price', monthlyPotential: 'monthly-potential'},
      items: [],
      total: 0,
      summary: {assets: 0, organizations: 0, monthlyPotential: 0},
      category: this.$route.query.category || null,
      loading: false,
      drawer: false,
      detail: null,
      options: {},
    }
  },

  computed: {
    headers() {
      return [
        {text: this.$t('billing-control.table.organization'), value: 'organization', sortable: false},
        {text: this.$t('billing-control.opportunities.asset-type'), value: 'assetType', sortable: false},
        {text: this.$t('billing-control.opportunities.reason'), value: 'reason', sortable: false},
        {text: this.$t('billing-control.opportunities.missing-licence'), value: 'category', sortable: false},
        {text: this.$t('billing-control.opportunities.asset-count'), value: 'assetCount', align: 'end'},
        {text: this.$t('billing-control.opportunities.unit-monthly-price'), value: 'unitMonthlyPrice', align: 'end'},
        {text: this.$t('billing-control.opportunities.potential'), value: 'monthlyPotential', align: 'end'},
        {text: '', value: 'actions', sortable: false, align: 'end'},
      ]
    },

    categoryItems() {
      return ['supervision', 'expense', 'energy-provider', 'connected-vehicle'].map(c => ({
        text: this.$t('enums.subscription-plan-types.' + c),
        value: c,
      }))
    },
  },

  watch: {
    options: {deep: true, handler() { this.loadData() }},
  },

  methods: {
    loadData() {
      this.loading = true
      const data = {
        category: this.category,
        offset: this.options.itemsPerPage > 0 ? (this.options.page - 1) * this.options.itemsPerPage : 0,
        limit: this.options.itemsPerPage || 25,
        orderBy: (this.options.sortBy && this.options.sortBy[0]) || 'monthlyPotential',
        orderDirection: (this.options.sortDesc && this.options.sortDesc[0]) ? 'desc' : 'asc',
      }
      BillingControlRepository.revenueOpportunities(data).then(res => {
        this.items = res.items || []
        this.total = res.total || 0
        this.summary = res.summary || {assets: 0, organizations: 0, monthlyPotential: 0}
        this.loading = false
      }).catch(err => {
        this.notifyError(err)
        this.loading = false
      })
    },

    openDetail(row) {
      this.detail = row
      this.drawer = true
    },

    categoryLabel(category) {
      return category ? this.$t('enums.subscription-plan-types.' + category) : '—'
    },

    /** Le motif dit l'équipement : « borne à domicile… » ou « compteur à domicile… ». */
    reasonLabel(row) {
      return this.$t('billing-control.opportunities.reasons.' + row.reason + '.' + row.assetType)
    },

    assetRoute(row, asset) {
      return row.assetType === 'meter-device'
        ? { name: 'meter-device.detail', params: { id: asset.id } }
        : { name: 'charging-points.detail.licences', params: { id: asset.id } }
    },

    reload() {
      this.options = {...this.options, page: 1}
    },
  },
}
</script>

<style lang="scss" scoped>
.asset-row {
  padding: 8px 0;
  border-bottom: 1px solid #eee;
}
</style>
